import React, { useContext, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  Pagination,
  Select,
  MenuItem
} from '@mui/material'
import FixedWidthContainer from 'components/FixedWidthContainer'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { CartContext } from 'contexts/CartContext'
import useSearchProducts from 'hooks/useSearchProducts'
import SectionHeader from 'components/SectionHeader'
import { PRODUCT_IMAGE_BASE_URL } from 'config'
import { useNavigate } from 'react-router-dom'

type SearchProps = {
  keyword: string
}

const SearchPage: React.FC<SearchProps> = ({ keyword }) => {
  const { updateCartItem } = useContext(CartContext)
  const navigate = useNavigate()

  const [quantities, setQuantities] = useState<{ [key: string]: number }>({})

  const { products, currentPage, totalPages, totalItems, setCurrentPage } =
    useSearchProducts({ keyword })

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const handleQuantityChange = (productId: string, value: number) => {
    setQuantities(prevQuantities => ({
      ...prevQuantities,
      [productId]: value
    }))
  }

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader
        title={<>SEARCH RESULTS FOR: &apos;{keyword.toUpperCase()}&apos;</>}
      />

      <Typography
        variant='h4'
        sx={{
          textAlign: 'center',
          mt: '10px',
          mb: '60px'
        }}
      >
        {totalItems} Items
      </Typography>

      <Grid container spacing={3}>
        {products.map(product => (
          <Grid item xs={12} sm={6} md={3} key={product.id}>
            <Box
              sx={{
                borderRadius: '10px',
                textAlign: 'center',
                background: '#F8F8F8',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}
            >
              <Box
                sx={{ justifyContent: 'center', cursor: 'pointer' }}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <Box
                  sx={{
                    width: '240px',
                    height: '240px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    component='img'
                    src={`${PRODUCT_IMAGE_BASE_URL}${product.imageIndex}.png`}
                    alt={product.sku}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{ flexGrow: 1, textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/products/${product.id}`)}
              >
                <Typography variant='h4'>{product.sku}</Typography>
                <Typography variant='body2'>{product.description}</Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  my: 1
                }}
              >
                <Typography variant='h4'>
                  ${Number(product.price).toFixed(2)}
                </Typography>
                {product.stock === 0 ? (
                  <Chip
                    label='Out of Stock'
                    size='small'
                    sx={{
                      backgroundColor: 'rgba(211,47,47,0.08)',
                      color: 'error.main',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '13px',
                      height: '28px'
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='body1'>Qty:</Typography>
                    <Select
                      value={quantities[product.id] || 1}
                      onChange={event =>
                        handleQuantityChange(
                          product.id,
                          Number(event.target.value)
                        )
                      }
                      sx={{
                        width: 50,
                        fontSize: '14px',
                        fontWeight: 700,
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '4px 8px'
                        },
                        '& .MuiSelect-icon': {
                          color: 'primary.main'
                        }
                      }}
                    >
                      {Array.from(
                        {
                          length: product.qtyRestrictions
                            ? Math.min(product.qtyRestrictions, product.stock)
                            : product.stock
                        },
                        (_, i) => i + 1
                      ).map(q => (
                        <MenuItem
                          key={q}
                          value={q}
                          sx={{ justifyContent: 'center' }}
                        >
                          <Typography variant='h3'>{q}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>

              {product.stock !== 0 && (
                <Button
                  variant='contained'
                  color='primary'
                  sx={{
                    width: '160px',
                    height: '40px',
                    borderRadius: '4px',
                    background: 'primary.main',
                    marginBottom: '10px',
                    alignSelf: 'center',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onClick={() =>
                    updateCartItem({
                      productId: product.id,
                      quantity: quantities[product.id] || 1,
                      isAssembled: false,
                      hingeDirection: ''
                    })
                  }
                >
                  <ShoppingBagIcon sx={{ transform: 'scale(0.8)' }} />
                  <Typography>Add to Bag</Typography>
                </Button>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color='primary'
          />
        </Box>
      )}
    </FixedWidthContainer>
  )
}

export default SearchPage
